import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import {USER_KEYS} from "@/api/userApi";
import EditIcon from "@/assets/colorable/edit.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useAgencyId, useIsAgency, useSelectedCompanyId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {AbilityContext} from "@/permissions";
import {useCompanyStore} from "@/store/company";
import useRegistrationStore from "@/store/registration";
import useTokenStore from "@/store/token";
import type {TCompanyProfile} from "@/store/types";
import useUserStore from "@/store/user";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useSearch} from "@tanstack/react-router";
import {type FC, useContext, useEffect, useState} from "react";
import {useDropzone} from "react-dropzone";

const BrandLogo: FC<{
  company?: TCompanyProfile;
  isCompanyProfile?: boolean;
}> = ({company: companyFromProps, isCompanyProfile = false}) => {
  const {access_token} = useSearch({strict: false});
  const queryClient = useQueryClient();
  const abilityContext = useContext(AbilityContext);
  const {refetch: refetchUser} = useFetchUser();
  const isAgency = useIsAgency();
  const agencyId = useAgencyId();
  const accessTokenFromStore = useTokenStore(state => state.access);
  const loggedInUserId = useUserStore(state => state.user.id);
  const registeringUserId = useRegistrationStore(state => state.userId);
  const companyFromStore = useCompanyStore(state => state.company);
  const company = companyFromProps ?? companyFromStore;
  const loggedInCompanyId = useSelectedCompanyId();
  const registeringCompanyId = useRegistrationStore(state => state.companyId);
  const registeringCompanyLogo = useRegistrationStore(state => state.companyLogo);
  const setRegistrationValue = useRegistrationStore(state => state.setValue);
  const companyId = isCompanyProfile ? company.id : (loggedInCompanyId ?? registeringCompanyId);
  const userId = isCompanyProfile ? company?.user?.id : (loggedInUserId ?? registeringUserId);
  const [file, setFile] = useState<File | null>(null);

  const onDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    setFile(acceptedFiles[0]);
  };

  const shownImage = file
    ? URL.createObjectURL(file!)
    : (company.picture ?? company.picture320 ?? registeringCompanyLogo ?? "");

  const {getInputProps, getRootProps, isDragActive, isDragReject} = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
  });

  const {mutate: uploadImages} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompany(companyId!),
    mutationFn: async () => {
      if (!file) return;
      const formData = new FormData();
      formData.append("picture", file);
      formData.append("picture640", file);
      formData.append("picture320", file);
      return await client.patch(COMPANY_KEYS.mCompany(companyId!)[0], formData, {
        headers: {
          Authorization: `Bearer ${accessTokenFromStore ?? access_token}`,
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: response => {
      queryClient.invalidateQueries({queryKey: USER_KEYS.qUser(Number(userId)), exact: true});

      if (isAgency) {
        queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencySubbrands(agencyId!)});
        refetchUser();
      }

      setRegistrationValue("companyLogo", response?.data?.picture);
    },
  });

  useEffect(() => {
    if (!file) return;
    uploadImages();
  }, [file, uploadImages]);

  if (abilityContext.cannot("update", "CompanyDetails")) {
    return !!shownImage ? (
      <div className="group border-primary-light-solid relative size-44 rounded-full border-2 shadow-md">
        <img src={shownImage} className="h-full w-full rounded-full object-cover" />
      </div>
    ) : null;
  }

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {!shownImage ? (
        <div
          className={clsxm(
            "group border-primary-dark-solid hover:border-primary-midnight size-44 rounded-full border-2 border-dashed bg-transparent object-cover transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <div className="flex h-full w-full items-center justify-center">
            {isDragReject ? (
              <TimesIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight text-danger-raspberry flex size-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            ) : (
              <PlusIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex size-12 items-center justify-center rounded-full border p-3.5 text-white shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className={clsxm(
            "group border-primary-light-solid hover:border-primary-midnight relative size-44 rounded-full border-2 shadow-md transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <img src={shownImage} alt="" className="h-full w-full rounded-full object-cover" />
          <div
            className={clsxm(
              "border-primary-light-solid group-hover:border-primary-midnight absolute top-0 right-0 flex size-12 items-center justify-center rounded-full border bg-white p-3 shadow-lg transition-all duration-150",
              isDragActive && !isDragReject && "border-primary-midnight bg-primary-midnight",
              isDragReject && "border-danger-medium-red bg-danger-light-red",
            )}>
            {isDragReject ? (
              <TimesIcon className="size-4 shrink-0" />
            ) : (
              <EditIcon
                className={
                  "size-4.5 transition-all duration-150 " +
                  (isDragActive && !isDragReject ? "text-white" : "text-primary-dark-solid")
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandLogo;
